import React, { useState, useEffect, useMemo } from "react";
import SkeletonCardBerita from "../../component/skeleton/CardBerita";
import axios from "axios";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import "dayjs/locale/id";
import "dayjs/locale/en";
import { useCookies } from "react-cookie";
import DownloadFormModal from "../modal/DownloadFormModal";

const Roadmap = ({ searchQuery = "" }) => {
    const [cookies] = useCookies(["i18next"]);
    const [loading, setLoading] = useState(true);
    const [posts, setPosts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 8;

    // State modal form download
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const openModal = (e, item) => {
        e.preventDefault();
        setSelectedItem(item);
        setShowModal(true);
    };

    // Reset halaman ke 1 setiap kali query pencarian berubah
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    // Format Tanggal
    const formatDate = (date, locale = "en") => {
        dayjs.locale(locale); // Set the locale dynamically
        return dayjs(date).format("DD MMMM YYYY"); // Format the date
    };

    // Fetch Data API
    useEffect(() => {
        const fetchPosts = async () => {
            setLoading(true);
            try {
                const url = process.env.REACT_APP_API_URL;
                const endpoint = process.env.REACT_APP_API_PUSTAKA;
                const response = await axios.get(`${url}${endpoint}`);

                // Filter kategori khusus untuk Roadmap (report_category_id === 1)
                const res = response.data.filter(
                    (post) => post.report_category_id === 1
                );
                setPosts(res);
            } catch (err) {
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: err.message,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    // Filter dan Sort Data berdasarkan Pencarian
    const filteredPosts = useMemo(() => {
        return posts
            .filter(
                (post) =>
                    post.title &&
                    post.title.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a, b) => {
                const da = a.date ? dayjs(a.date).valueOf() : 0;
                const db = b.date ? dayjs(b.date).valueOf() : 0;
                if (db !== da) return db - da; // Urutkan berdasarkan tanggal terbaru
                return (b.id || 0) - (a.id || 0); // Jika tanggal sama, urutkan berdasarkan ID
            });
    }, [posts, searchQuery]);

    // Kalkulasi Paginasi
    const lastPostIndex = currentPage * postsPerPage;
    const firstPostIndex = lastPostIndex - postsPerPage;
    const currentPosts = filteredPosts.slice(firstPostIndex, lastPostIndex);
    const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

    const handlePageChange = (pageNumber) => {
        if (pageNumber !== "...") setCurrentPage(pageNumber);
    };

    // Fungsi Generate Item Paginasi
    const generatePaginationItems = () => {
        const paginationItems = [];
        const maxPageNumbersToShow = 5;
        let startPage, endPage;

        if (totalPages <= maxPageNumbersToShow) {
            startPage = 1;
            endPage = totalPages;
        } else {
            if (currentPage <= 3) {
                startPage = 1;
                endPage = maxPageNumbersToShow;
            } else if (currentPage + 2 >= totalPages) {
                startPage = totalPages - maxPageNumbersToShow + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - 2;
                endPage = currentPage + 2;
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            paginationItems.push(i);
        }

        if (startPage > 1) paginationItems.unshift("...");
        if (endPage < totalPages) paginationItems.push("...");

        return paginationItems;
    };

    return (
        <>
            <div className="row row-gutter-y-30 mt-3">
                {loading ? (
                    Array(postsPerPage)
                        .fill()
                        .map((_, index) => (
                            <div className="col-12 col-md-6 col-xl-3" key={index}>
                                <SkeletonCardBerita />
                            </div>
                        ))
                ) : currentPosts.length > 0 ? (
                    currentPosts.map((item) => (
                        <div className="col-12 col-md-6 col-xl-3" key={item?.id}>
                            <div className="team-card-x">
                                <div className="team-card-img-x">
                                    <a href={`/e-pustaka/${item?.id}`}>
                                        <img
                                            src="/assets/image/book1.jpeg"
                                            className="img-fluid"
                                            alt="img-40"
                                        />
                                    </a>
                                </div>
                                <div className="team-card-content-x">
                                    <h4>
                                        <a href={`/e-pustaka/${item?.id}`}>
                                            {cookies.i18next === "en" ? item?.title_en : item?.title}
                                        </a>
                                    </h4>
                                    <div className="d-flex justify-content-between align-items-end">
                                        <p>
                                            {cookies.i18next === "id"
                                                ? formatDate(item.date, "id")
                                                : formatDate(item.date, "en")}
                                        </p>
                                        <a
                                            title="Downloadable"
                                            href="#download"
                                            onClick={(e) => openModal(e, item)}
                                        >
                                            <i
                                                className="fa-solid fa-download"
                                                aria-hidden="true"
                                            ></i>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div
                        className="col-lg-12 col-md-12"
                        style={{ marginBottom: "200px" }}
                    >
                        <p className="text-center">No posts available</p>
                    </div>
                )}

                {!loading && totalPages > 1 && (
                    <div className="col-12 mt-4 d-flex justify-content-center">
                        <div
                            className="pagination d-flex align-items-center"
                            style={{ gap: "8px" }}
                        >
                            <button
                                className="pagination-btn btn btn-sm btn-outline-secondary"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fa-solid fa-chevron-left"></i> Prev
                            </button>
                            {generatePaginationItems().map((page, index) => (
                                <button
                                    key={index}
                                    className={`pagination-btn ${page === currentPage ? "active" : ""}`}
                                    onClick={() => handlePageChange(page)}
                                    disabled={page === "..."}
                                    style={{ minWidth: "36px" }}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                className="pagination-btn btn btn-sm btn-outline-secondary"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <DownloadFormModal
                show={showModal}
                item={selectedItem}
                onHide={() => setShowModal(false)}
            />
        </>
    );
};

export default Roadmap;